import React, { useEffect, useState } from 'react';
import { PlayCircle } from 'lucide-react';
import SuccessStoriesCarousel from '../../../components/start/SuccessStoriesCarousel';
import LogoLoop from '../../../components/LogoLoop';
import {
  SUCCESS_PARTNER_LOGOS,
  SUCCESS_PARTNER_GALLERY,
  SUCCESS_TESTIMONIAL_VIDEOS,
} from '../../StartSuccessStories';
import { welcomeGlass, welcomeEyebrow, welcomeSectionTitle } from '../welcomeTokens';

// Matches the glass card fill so LogoLoop's edge fade blends into the strip.
const LOGO_FADE_COLOR = '#16151a';

/**
 * "Success Stories" popup for /welcome/JTBootcamp — carousel, partners and
 * testimonials imported from the /start subpage, restyled into the welcome
 * page's dark glass language. The `dark` wrapper pins the shared carousel's
 * `dark:` utilities on, since this page never follows the viewer's theme.
 */
const WelcomeSuccessPanel = () => {
  const [storyIndex, setStoryIndex] = useState(0);
  const [revenueRange, setRevenueRange] = useState('0-1M');

  useEffect(() => {
    setStoryIndex(0);
  }, [revenueRange]);

  return (
    <div className="dark mx-auto w-full max-w-5xl space-y-12 px-4 py-8 sm:px-6 sm:py-10">
      <section>
        <p className={`${welcomeEyebrow} text-center`}>Results by revenue band</p>
        <h2 className="mt-2 text-center text-2xl font-bold tracking-tight text-white sm:text-3xl">
          What Happens After BBA
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-sm text-zinc-500">
          Pick a revenue range to see where those builders started and where they
          landed.
        </p>
        {/* Room on the sides for the carousel's outdented arrows. */}
        <div className="mt-8 px-0 sm:px-14">
          <SuccessStoriesCarousel
            theme="welcome"
            successStoriesIndex={storyIndex}
            setSuccessStoriesIndex={setStoryIndex}
            selectedRevenueRange={revenueRange}
            setSelectedRevenueRange={setRevenueRange}
          />
        </div>
      </section>

      <section>
        <h3 className={`${welcomeEyebrow} text-center`}>Builders we work with</h3>
        <div
          className={`relative mt-4 flex items-center overflow-hidden rounded-2xl ${welcomeGlass}`}
          style={{ height: 150 }}
        >
          <LogoLoop
            logos={SUCCESS_PARTNER_LOGOS}
            speed={40}
            direction="left"
            logoHeight={90}
            gap={80}
            hoverSpeed={0}
            scaleOnHover
            fadeOut
            fadeOutColor={LOGO_FADE_COLOR}
            ariaLabel="Partner logos"
          />
        </div>
      </section>

      <section>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {SUCCESS_PARTNER_GALLERY.map((pic) => (
            <figure
              key={pic.src}
              className="group relative overflow-hidden rounded-2xl border border-white/10 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
            >
              <img
                src={pic.src}
                alt={pic.alt}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div
                className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                aria-hidden
              />
              <figcaption className="absolute inset-x-0 bottom-0 translate-y-2 px-3 py-2.5 text-xs font-semibold text-white opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                {pic.alt}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section>
        <h3 className={`${welcomeSectionTitle} flex items-center justify-center gap-2`}>
          <PlayCircle className="h-5 w-5 text-red-500" aria-hidden />
          Hear It From Them
        </h3>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {SUCCESS_TESTIMONIAL_VIDEOS.map((video) => (
            <div key={video.src} className={`rounded-2xl p-4 ${welcomeGlass}`}>
              <div className="overflow-hidden rounded-xl border border-white/10 bg-black">
                <iframe
                  src={video.src}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="aspect-video w-full"
                />
              </div>
              <p className="mt-3 text-center text-sm font-semibold text-white">
                {video.company}
              </p>
              <p className={`mt-0.5 text-center ${welcomeEyebrow}`}>
                In their own words
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default WelcomeSuccessPanel;
